import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Baie coulissante', to: '/reparation-baie-coulissante-herault/' },
  { label: 'Réglage baie' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Réglage de baie coulissante dans l\'Hérault',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'AdministrativeArea', name: 'Hérault' },
  description: 'Réglage de baie coulissante : alignement, hauteur, compression de joint, fermeture étanche. Intervention dans l\'Hérault.',
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Comment savoir si ma baie coulissante a besoin d\'un réglage ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Les principaux signes sont : une baie qui frotte anormalement en haut ou en bas du cadre, un jour visible entre le vantail et le dormant, des infiltrations d\'air ou d\'eau, ou une difficulté à verrouiller la baie car le pêne ne s\'engage plus correctement dans la gâche. Un réglage professionnel résout la plupart de ces problèmes.',
      },
    },
    {
      '@type': 'Question',
      name: 'Un réglage suffit-il ou faut-il changer des pièces ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Dans de nombreux cas, un simple réglage suffit à restaurer le fonctionnement optimal de la baie. Cependant, si l\'usure des roulettes ou du rail est en cause, un remplacement de pièces peut être nécessaire. C\'est pourquoi nos techniciens réalisent toujours un diagnostic complet avant toute intervention, et ne vous recommandent que les travaux strictement nécessaires.',
      },
    },
  ],
};

const contentBlocks = [
  {
    image: '/images/reglage-block1.jpg',
    imageAlt: 'Baie coulissante desalignee necessitant un reglage dans l Herault',
    children: (
      <>
        <h2>Le réglage, une intervention souvent négligée</h2>
        <p>
          Le réglage d'une baie coulissante est une opération technique qui fait toute la différence entre une menuiserie qui fonctionne parfaitement et une baie qui accroche, laisse passer l'air ou refuse de se verrouiller. Trop souvent, les occupants d'un logement s'habituent à ces petits défauts sans réaliser qu'un réglage professionnel peut les corriger en moins d'une heure. Dans l'Hérault, où les baies coulissantes sont très présentes dans l'habitat — des villas contemporaines de Montpellier aux appartements avec terrasse de Sète ou de La Grande-Motte — un réglage régulier permet de prolonger significativement la durée de vie de votre menuiserie.
        </p>
        <h3>Les paramètres de réglage d'une baie coulissante</h3>
        <p>
          Une baie coulissante aluminium dispose de plusieurs points de réglage, chacun jouant un rôle précis dans le fonctionnement d'ensemble. La hauteur du vantail est réglable via les chariots de roulement : un vantail trop bas frotte sur le rail, un vantail trop haut laisse passer l'air et compromet l'étanchéité. L'alignement latéral assure que le vantail est parallèle au dormant, garantissant une compression uniforme des joints sur toute la hauteur. Le réglage de la compression du joint détermine l'effort nécessaire pour manœuvrer la baie et l'efficacité de l'isolation thermique et phonique. Enfin, le réglage des points de verrouillage — crémone haute et basse, crochets de sécurité — assure une fermeture parfaite et sécurisée.
        </p>
      </>
    ),
  },
  {
    image: '/images/reglage-block2.webp',
    imageAlt: 'Technicien DFPRO effectuant le reglage d une baie coulissante',
    imagePosition: 'right' as const,
    children: (
      <>
        <h3>Quand faut-il faire régler sa baie coulissante ?</h3>
        <p>
          Plusieurs situations justifient un réglage. Si vous sentez un courant d'air près de votre baie fermée, c'est que les joints ne compressent plus correctement. Si la baie frotte anormalement en partie haute ou basse, l'alignement est à reprendre. Si vous devez soulever le vantail pour le faire coulisser, la hauteur de roulement est mal réglée. Si la clé ou la poignée force au verrouillage, les points de crémone doivent être ajustés. Tous ces symptômes peuvent être corrigés par un réglage professionnel.
        </p>
        <h3>Réglage après remplacement de pièces</h3>
        <p>
          Un réglage est systématiquement nécessaire après le <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement des roulettes et galets</Link> ou après une <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation du rail de coulissement</Link>. Les nouvelles pièces modifient la géométrie du vantail, et un ajustement méticuleux est indispensable pour retrouver un coulissement fluide et une étanchéité parfaite. C'est une étape que nos techniciens DFPRO Hérault ne négligent jamais.
        </p>
        <h3>Les bénéfices d'un bon réglage</h3>
        <p>
          Au-delà du confort d'usage, un réglage correct de votre baie coulissante a des bénéfices concrets : meilleure isolation thermique, donc économies sur votre facture énergétique ; isolation phonique renforcée, particulièrement appréciable en milieu urbain à Montpellier ou Béziers ; sécurité optimale grâce à un verrouillage qui s'engage parfaitement ; et longévité accrue de l'ensemble des composants de la menuiserie. Un réglage préventif coûte toujours moins cher qu'une réparation curative.
        </p>
        <p>
          Pour toute question sur nos services de réglage ou pour planifier une intervention dans l'Hérault, contactez-nous au 09 80 80 22 42. Consultez également notre page principale sur la <Link to="/reparation-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation de baie coulissante</Link>.
        </p>
      </>
    ),
  },
];

export default function ReglageBaie() {
  return (
    <>
      <SEO
        title="Réglage de baie coulissante dans l'Hérault | DFPRO"
        description="Baie coulissante mal alignée, joint qui ne comprime plus, fermeture difficile dans l'Hérault ? Réglage précis par nos techniciens."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/reglage-baie-coulissante-herault/"
      />
      <ServicePageLayout
        title="Réglage de baie vitrée coulissante dans l'Hérault"
        subtitle="Alignement, hauteur, compression du joint : nos techniciens règlent votre baie coulissante pour un fonctionnement optimal dans tout l'Hérault."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        heroImage="/images/reglage-hero.webp"
        heroImageAlt="Reglage de baie coulissante DFPRO Herault"
        contentBlocks={contentBlocks}
      />
    </>
  );
}